import FetchBeneficiaryTypeGrants from '@Application/SubjectTypeDefinitions/Queries/FetchBeneficiaryTypeGrants';
import FetchSubjectTypeAccessLevels from '@Application/SubjectTypeDefinitions/Queries/FetchSubjectTypeAccessLevels';
import fetchBeneficiaryTypeGrants from '@Application/SubjectTypeDefinitions/fetchBeneficiaryTypeGrants';
import fetchSubjectTypeAccessLevels from '@Application/SubjectTypeDefinitions/fetchSubjectTypeAccessLevels';
import ErrorState from '@Guidelines/Component/ErrorState';
import { theme } from '@Guidelines/Theme/theme';
import BeneficiaryTypeAccessEditor from '@UI/Pages/Administration/SubjectTypes/BeneficiaryTypeAccess/BeneficiaryTypeAccessEditor';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import { css } from '@emotion/react';
import * as React from 'react';
import { STATIC_CACHE, useDispatch, useStoreQuery } from '@verclam/synapse';
import { Skeleton } from '@verclam/ui-kit';

const skeletonStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

/**
 * Resolves what the editor needs before mounting it: the access lines and the access levels. The
 * editor opens on the copy the store holds; writes keep it in sync.
 */
const BeneficiaryTypeAccessContent: React.FC = () => {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;

	const {
		data: grants,
		error: grantsError,
		refetch: refetchGrants,
	} = useStoreQuery({
		selector: (state) => state.subjectTypeDefinitionsStore.beneficiaryTypeGrants,
		stopOn: (value) => accountId === null || (value !== null && value.accountId === accountId),
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchBeneficiaryTypeGrants(new FetchBeneficiaryTypeGrants(accountId)));
		},
		deps: [accountId],
	});

	const {
		data: accessLevels,
		error: accessLevelsError,
		refetch: refetchAccessLevels,
	} = useStoreQuery({
		selector: (state) => state.subjectTypeDefinitionsStore.accessLevels,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchSubjectTypeAccessLevels(new FetchSubjectTypeAccessLevels())),
		cache: STATIC_CACHE,
	});

	const handleRetry = React.useCallback(() => {
		refetchGrants();
		refetchAccessLevels();
	}, [refetchGrants, refetchAccessLevels]);

	if (grants === null && grantsError !== null) {
		return <ErrorState title="Impossible de charger l'accès aux bénéficiaires" onRetry={handleRetry} />;
	}

	if (accessLevels === null && accessLevelsError !== null) {
		return <ErrorState title="Impossible de charger les niveaux d'accès" onRetry={handleRetry} />;
	}

	if (grants === null || accessLevels === null) {
		return (
			<div css={skeletonStyle}>
				<Skeleton height="12rem" borderRadius={theme.radii.xlarge} animation="wave" />
			</div>
		);
	}

	return <BeneficiaryTypeAccessEditor grants={grants} />;
};

export default BeneficiaryTypeAccessContent;
